import { ChildModel, ModelBase } from 'store/model-base';
import { LineItemCharge } from './charge';
import { DeviceType } from 'my-account/constants';
import { Util } from 'services/util';

export class SsmStatementLineItem extends ModelBase {
    public static storeName: string = 'ssmStatementLineItem';

    public lineTotal: number;
    public lineId: string;
    public lineIdKey: string;
    public lineNumber: string;
    public deviceType: string;
    public displayName: string;
    public charges: LineItemCharge[];
    public lineServiceTotal: number;
    public lineType: string;

    public isPhone: boolean;
    public isTablet: boolean;
    public isWatch: boolean;
    public isStandaloneWatch: boolean;

    public unlimitedCharge: LineItemCharge;
    public byTheGigCharge: LineItemCharge;

    public domesticCharge: LineItemCharge;
    public noWireLineCharge: LineItemCharge;
    public intlTalkTextCharge: LineItemCharge;
    public roamingCharge: LineItemCharge;

    public hasByTheGigCharge: boolean;
    public hasUnlimitedCharge: boolean;

    public hasDomesticCharge: boolean;
    public hasInternationalCharge: boolean;
    public hasNoWireLineCharge: boolean;

    public internationalTotal: number;
    public byTheGigDataUsage: number;
    public totalDomesticCalls: number;
    public totalDomesticMessages: number;
    public totalInternationCalls: number;
    public totalInternationMessages: number;
    public totalRoamingCalls: number;
    public totalRoamingMessages: number;
    public totalRoamingData: number;

    public hasSharedData: boolean;
    public lineServicePrimaryCharge: LineItemCharge;

    public mexCanPass: LineItemCharge;
    public totalMexCanCalls: number;
    public hasMexCanPass: boolean;
    public isMobilePlus: boolean;

    public static create<T extends ModelBase>(data: ApiResponse): T {

        data.isPhone = data.deviceType?.toUpperCase() === DeviceType.PHONE;
        data.isTablet = data.deviceType?.toUpperCase() === DeviceType.TABLET;
        data.isWatch = data.deviceType?.toUpperCase() === DeviceType.SMART_WATCH && Boolean(!data.lineNumber);
        data.isStandaloneWatch = data.deviceType?.toUpperCase() === DeviceType.SMART_WATCH && Boolean(data.lineNumber);
        
        const toReturn: SsmStatementLineItem = super.create<SsmStatementLineItem>(data);
        // Comuting properties whcihc require model properties of child models
        toReturn.unlimitedCharge = toReturn.charges.find((charge: LineItemCharge) => charge.isLineServicePrimary && charge.planDetails.isUnlimited);
        toReturn.byTheGigCharge = toReturn.charges.find((charge: LineItemCharge) => charge.isLineServicePrimary && charge.planDetails.isByTheGig);

        toReturn.domesticCharge = toReturn.charges.find((charge: LineItemCharge) => charge.isDomesticCharge);
        toReturn.noWireLineCharge = toReturn.charges.find((charge: LineItemCharge) => charge.isNoWireline);
        toReturn.intlTalkTextCharge = toReturn.charges.find((charge: LineItemCharge) => charge.isInternationalCharge);
        toReturn.roamingCharge = toReturn.charges.find((charge: LineItemCharge) => charge.isRoamingCharge);
        toReturn.mexCanPass = toReturn.charges.find((charge: LineItemCharge) => charge.isMexCanCharge);

        toReturn.hasByTheGigCharge = Boolean(toReturn.byTheGigCharge);
        toReturn.hasUnlimitedCharge = Boolean(toReturn.unlimitedCharge);
        toReturn.hasDomesticCharge = Boolean(toReturn.domesticCharge);
        toReturn.hasNoWireLineCharge = Boolean(toReturn.noWireLineCharge);
        toReturn.hasInternationalCharge = Boolean(toReturn.intlTalkTextCharge) || Boolean(toReturn.roamingCharge);
        toReturn.hasMexCanPass = Boolean(toReturn.mexCanPass);

        // Compute international total
        const talkTextPrice: number = toReturn.intlTalkTextCharge ? toReturn.intlTalkTextCharge.price : 0;
        const roamingPrice: number = toReturn.roamingCharge ? toReturn.roamingCharge.price : 0;
        toReturn.internationalTotal = Util.sumFloat([ talkTextPrice, roamingPrice ]);

        toReturn.byTheGigDataUsage = toReturn.hasByTheGigCharge ? toReturn.byTheGigCharge.usageDetail.dataConsumed : 0;
        toReturn.totalDomesticCalls = toReturn.domesticCharge ? toReturn.domesticCharge.usageDetail.voiceVolume : 0;
        toReturn.totalDomesticMessages = toReturn.domesticCharge ? toReturn.domesticCharge.usageDetail.textVolume : 0;
        toReturn.totalInternationCalls = toReturn.intlTalkTextCharge ? toReturn.intlTalkTextCharge.usageDetail.voiceVolume : 0;
        toReturn.totalInternationMessages = toReturn.intlTalkTextCharge ? toReturn.intlTalkTextCharge.usageDetail.textVolume : 0;
        toReturn.totalRoamingCalls = toReturn.roamingCharge ? toReturn.roamingCharge.usageDetail.voiceVolume : 0;
        toReturn.totalRoamingMessages = toReturn.roamingCharge ? toReturn.roamingCharge.usageDetail.textVolume : 0;
        toReturn.totalRoamingData = toReturn.roamingCharge ? toReturn.roamingCharge.usageDetail.dataConsumed : 0;
        toReturn.totalMexCanCalls = toReturn.mexCanPass ? toReturn.mexCanPass.usageDetail.voiceVolume : 0;

        // Check for shared data
        toReturn.lineServicePrimaryCharge = toReturn.charges.find((charge: LineItemCharge) => charge.isLineServicePrimary);
        toReturn.hasSharedData = toReturn.lineServicePrimaryCharge && toReturn.lineServicePrimaryCharge.hasSharedData;

        // Shuffle charges to have domestic charge on top
        toReturn.charges = [ toReturn.domesticCharge, ...toReturn.charges.filter((charge: LineItemCharge) => !charge.isDomesticCharge) ];

        /* eslint-disable @typescript-eslint/no-explicit-any */
        return <T> <any> toReturn;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    protected static get hasMany(): ChildModel[] {
        return [{
            attrName: 'charges',
            model: LineItemCharge
        }];
    }
}